import { useTypedEvent } from "@faro-lotv/app-component-toolbox";
import type { GUID, TypedEvent } from "@faro-lotv/foundation";
import {
  RefObject,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  type JSX,
} from "react";
import { Vector3 } from "three";
import { ToolControlsRef } from "../tool-controls-interface";
import { useLensCursor } from "../use-lens-cursor";
import {
  MeasurementCompletedEvent,
  MultiPointMeasureControlsLogic,
  ProjectionType,
} from "./multi-point-measures-controls-logic";
import { useAttachCallbacksToControls } from "./use-attach-callbacks-to-controls";
import { useForwardKeyEventsToControls } from "./use-forward-key-events-to-controls";

export type MultiPointMeasureControlsActions = {
  /**
   * Complete the current interaction
   *
   * @param isClosed Flag specifying if the measurement is a closed polygon
   * @param isCompletedByClickFirstPoint if true, polygon is closed by click on first point
   */
  completeMeasurement(
    isClosed: boolean,
    isCompletedByClickFirstPoint?: boolean,
  ): void;

  /**
   * Delete a point from the current polygon
   *
   * @param index The index of the point to remove
   */
  deletePoint(index: number): void;

  /** Remove the latest picked point of the measurement being taken */
  undoPoint(): void;

  /** Restore the latest point removed with undoPoint */
  redoPoint(): void;

  /** @returns true if there is a removed point that redoPoint can restore */
  canRedo(): boolean;
};

type MultiPointMeasureControlsProps = {
  /** Callback issued when a picked point changes */
  onCurrentPointChanged(point: Vector3 | undefined): void;
  /** Callback issued when both points are defined */
  onMeasurementCompleted(event: MeasurementCompletedEvent): void;
  /** Callback issued when the currently selected measurement should be deleted */
  onDeleteActiveMeasurement(): void;
  /** Callback issued when a measurement is initiated */
  onMeasurementStarted?(): void;
  /** Callback issued when the user presses ESC key */
  onEscPressed?(): void;
  /** Callback issued when the array of points changes */
  onPointsChanged?(points: Vector3[] | undefined): void;
  /** Callback issued when the measurement is canceled */
  onMeasurementCanceled?(): void;
  /** Callback issued when the controller changes the projection type (e.g. responding to the Shift key being pressed) */
  onChangeProjectionType?(projectionType: ProjectionType): void;
  /** The actions that can be called from outside the controls */
  actions?: RefObject<MultiPointMeasureControlsActions | null>;
  /** Whether the control is active or not */
  active: boolean;
  /** Type of projection to apply when the shift key is pressed */
  projectOnShiftKey: ProjectionType;
  /** If provided, limit number of measurements by given number of points */
  maxPoints?: number;
  /** Optional signal triggered when the user clicks the zoom lens with a valid 'green' target */
  onValidClickInZoomLens?: TypedEvent<GUID>;
};

export const MultiPointMeasureControls = forwardRef<
  ToolControlsRef,
  MultiPointMeasureControlsProps
>(function MultiPointMeasureControls(
  {
    onCurrentPointChanged,
    onMeasurementCompleted,
    onDeleteActiveMeasurement,
    onMeasurementStarted,
    onEscPressed,
    onPointsChanged,
    onMeasurementCanceled,
    onChangeProjectionType,
    actions,
    active,
    projectOnShiftKey,
    maxPoints,
    onValidClickInZoomLens,
  }: MultiPointMeasureControlsProps,
  ref,
): JSX.Element {
  // Creating the controls logic
  const controls = useMemo(() => new MultiPointMeasureControlsLogic(), []);

  const completeMeasurement = useCallback(
    (isClosed: boolean, isCompletedByClickFirstPoint?: boolean) => {
      controls.complete(isClosed, isCompletedByClickFirstPoint);
    },
    [controls],
  );

  const deletePoint = useCallback(
    (index: number) => {
      controls.deletePoint(index, true);
    },
    [controls],
  );

  useImperativeHandle(actions, () => ({
    completeMeasurement,
    deletePoint,
    undoPoint: () => controls.deleteLatestPoint(),
    redoPoint: () => controls.redoLatestPoint(),
    canRedo: () => controls.canRedo(),
  }));

  useAttachCallbacksToControls(
    controls,
    onCurrentPointChanged,
    onMeasurementCompleted,
    onPointsChanged,
    onMeasurementStarted,
    onMeasurementCanceled,
    maxPoints,
  );

  useForwardKeyEventsToControls(
    controls,
    onDeleteActiveMeasurement,
    onEscPressed,
    projectOnShiftKey,
    onChangeProjectionType,
  );

  const forcePointClick = useCallback(
    (id: GUID) => controls.forcePointClick(id),
    [controls],
  );

  useTypedEvent(active ? onValidClickInZoomLens : undefined, forcePointClick);

  useEffect(() => {
    // initialize the tool activated
    controls.initialize();
  }, [active, controls]);

  useLensCursor(active);

  return <primitive ref={ref} object={controls} />;
});
